import React, { isValidElement, useCallback } from 'react'
import { ImageListItem, styled, Checkbox, Box } from '@mui/material'
import { useCreatePath, useTranslate } from 'react-admin'
import { ImageGridItemProps } from './type'
import { useNavigate } from 'react-router-dom'
import { createOrCloneElement } from '../../utils/component'

const ImageListItemStyled = styled(ImageListItem)(({ theme }) => ({
  background: theme.palette.common.black,
  ['& img']: {
    opacity: 1,
    transition: `opacity ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
  },
  ['&:hover']: {
    ['& img']: {
      opacity: 0.8,
    },
  },
}))

const BoxStyled = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 2,
}))

export const ImageGridItem: React.FC<ImageGridItemProps> = ({
  children,
  record,
  sx = {},
  hasBulkActions = false,
  rowClick,
  resource,
  id,
  selected = false,
  selectable = true,
  onToggleItem,
  ...props
}) => {
  const translate = useTranslate()
  const createPath = useCreatePath()
  const navigate = useNavigate()

  const handleToggleSelection = useCallback(
    (event) => {
      if (!selectable) return
      onToggleItem(id, event)
      event.stopPropagation()
    },
    [id, onToggleItem, selectable],
  )

  const handleClick = useCallback(
    async (event) => {
      event.persist()
      const type = typeof rowClick === 'function' ? await rowClick(id, resource, record) : rowClick
      if (type === false || type == null) {
        return
      }
      if (['edit', 'show'].includes(type)) {
        navigate(createPath({ resource, id, type }))
        return
      }
      if (type === 'toggleSelection') {
        handleToggleSelection(event)
        return
      }
      navigate(type)
    },
    [rowClick, id, resource, record, navigate, createPath, handleToggleSelection],
  )
  return (
    <ImageListItemStyled
      sx={{ cursor: rowClick ? 'pointer' : 'initial', ...sx }}
      {...props}
      onClick={handleClick}
    >
      {hasBulkActions && (
        <BoxStyled>
          {selectable && (
            <Checkbox
              aria-label={translate('ra.action.select_row', {
                _: 'Select this item',
              })}
              color='primary'
              checked={selected}
              onClick={handleToggleSelection}
            />
          )}
        </BoxStyled>
      )}
      {React.Children.map(children, (field, index) =>
        isValidElement(field)
          ? createOrCloneElement(
              field,
              {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                key: `${id}-${(field.props as any).source || index}`,
                record,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(field.props as any).props,
              },
              null,
            )
          : null,
      )}
    </ImageListItemStyled>
  )
}
